import type { Session } from '@supabase/supabase-js';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@nuberu/ui';
import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';

import { ApiError } from '@/lib/api';
import { getSupabaseClient } from '@/lib/supabase';
import { fetchSystemInfo, type SystemInfo } from '@/system/fetch-system';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: SystemInfo };

const Row = ({ label, children }: { label: string; children: ReactNode }): ReactElement => (
  <div className="flex items-center justify-between gap-4 border-b border-border-subtle py-2.5 last:border-b-0">
    <span className="text-sm text-text-secondary">{label}</span>
    <span className="text-sm font-medium text-text-primary">{children}</span>
  </div>
);

export const SystemPanel = ({ session }: { session: Session }): ReactElement => {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(
    (signal?: AbortSignal): void => {
      setState({ status: 'loading' });
      void fetchSystemInfo(session, signal)
        .then((data) => {
          if (!signal?.aborted) setState({ status: 'ready', data });
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          const message =
            err instanceof ApiError
              ? err.status === 403
                ? 'Tu cuenta no tiene acceso al backoffice.'
                : err.message
              : 'No pudimos cargar el estado del sistema.';
          setState({ status: 'error', message });
        });
    },
    [session],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const signOut = (): void => {
    void getSupabaseClient().auth.signOut();
  };

  const email = typeof session.user.email === 'string' ? session.user.email : '';

  return (
    <div className="min-h-screen bg-surface-2 p-4 sm:p-8">
      <div className="mx-auto flex max-w-xl flex-col gap-6">
        <header className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-text-primary">Backoffice</h1>
            <p className="text-sm text-text-secondary">{email}</p>
          </div>
          <Button variant="neutral" size="sm" onClick={signOut}>
            Cerrar sesión
          </Button>
        </header>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Sistema</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => load()}
              disabled={state.status === 'loading'}
            >
              {state.status === 'loading' ? 'Actualizando…' : 'Actualizar'}
            </Button>
          </CardHeader>
          <CardContent>
            {state.status === 'loading' ? (
              <p className="py-4 text-sm text-text-secondary">Cargando estado…</p>
            ) : state.status === 'error' ? (
              <p className="py-4 text-sm text-status-warning" role="alert">
                {state.message}
              </p>
            ) : (
              <div className="flex flex-col">
                <Row label="Entorno">
                  <Badge tone={state.data.env === 'production' ? 'solid' : 'neutral'}>
                    {state.data.env}
                  </Badge>
                </Row>
                <Row label="Commit">
                  <code className="rounded bg-surface-3 px-1.5 py-0.5 font-mono text-xs">
                    {state.data.commit}
                  </code>
                </Row>
                <Row label="IA · proveedor">{state.data.ai.provider}</Row>
                <Row label="IA · modelo">
                  <code className="font-mono text-xs">{state.data.ai.model}</code>
                </Row>
                <Row label="Base de datos">
                  <Badge tone={state.data.db === 'ok' ? 'success' : 'warning'}>
                    {state.data.db === 'ok' ? 'OK' : 'Caída'}
                  </Badge>
                </Row>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
